// Centralized scorecard data - replace with actual computed values from backend
// This file contains all example/mock data used across scorecard components

// User and Company Data Types
export interface UserData {
  id: string;
  username: string;
  email: string;
  initials: string;
}

export interface CompanyData {
  name: string;
  stateAbbrev: string;
  storeLocations: string[];
}

// User and Company Data - replace with database data in production
export const currentUserData: UserData = {
  id: '1',
  username: 'john.doe',
  email: 'john.doe@bespoke.ai',
  initials: 'JD'
};

export const companyData: CompanyData = {
  name: 'Gen Korean BBQ',
  stateAbbrev: 'CA',
  storeLocations: ['Fremont', 'Concord', 'San Jose', 'Oakland', 'Berkeley']
};

// Types for scorecard data
export interface RevelMetricAttribution {
  upsellCount: number;
  purchaseCount: number;
  totalValue: number;
}

export interface ServerScorecardEntry {
  date: string;
  server: string;
  greetings: number;
  score: number;
  premiumMenu: number;
  itemMention: number;
  itemDescription: number;
  drinkPromotions: number;
  lunch: string;
  revelAttribution?: {
    metric_1: RevelMetricAttribution;
    metric_2: RevelMetricAttribution;
    metric_3: RevelMetricAttribution;
    drink_promotion: RevelMetricAttribution;
  };
}

export interface CashierScorecardEntry {
  date: string;
  menuItem: string;
  upsellAttempts: number;
  averageUpsellRating: number;
  attemptRatio: number;
}

export interface GreeterScorecardEntry {
  date: string;
  rating1: number;
  rating2: number;
  rating3: number;
  interactions: number;
  averageRating: number;
}

export interface GreeterUpsellEntry {
  date: string;
  menuItem: string;
  upsellAttempts: number;
  averageUpsellRating: number;
  attemptRatio: number;
}

export interface StoreScorecardEntry {
  date: string;
  shift: string;
  // feat-093 Slice C: the persisted backend score, rendered VERBATIM. The client
  // no longer overrides this with a Revel-adjusted recompute (single-source).
  score: number;
  manager_id: string;
  shift_id?: string;
  location_id: string;
  location_name: string;
  // feat-093 Slice C: SUM of per-server-shift `score_breakdown.visit_count` for
  // this manager-shift (feat-092 greeting-anchored visit count, AC10 fallback →
  // greetings). Optional because pre-recalc `v:2` rows omit it; the "Visits" cell
  // reads `visit_count ?? greetings` (nullish, so a real 0 shows 0). Backend is
  // the single source — React never recomputes it.
  visit_count?: number | null;
  greetings?: number | null;
}

export interface ManagerScorecardEntry {
  date: string;
  shift: string;
  score: number;
  shiftId?: string;
}

export interface LeaderboardEntry {
  date: string;
  store: string;
  score: number;
  previousRank: number;
  currentRank: number;
  change?: number | null;
}

// Performance History Data Types
export interface LegacyServerPerformanceHistoryEntry {
  date: string;
  shift: string;
  greetings: number;
  score: number;
  premiumMenu: number;
  itemMention: number;
  itemDescription: number;
  drinkPromotions: number;
}

export interface CashierPerformanceHistoryEntry {
  date: string;
  shift: string;
  upsellAttempts: number;
  averageUpsellRating: number;
  attemptRatio: number;
}

export interface GreeterPerformanceHistoryEntry {
  date: string;
  shift: string;
  upsellAttempts: number;
  averageUpsellRating: number;
  attemptRatio: number;
}

export interface GreeterGreetingPerformanceHistoryEntry {
  date: string;
  shift: string;
  rating1: number;
  rating2: number;
  rating3: number;
  interactions: number;
  averageRating: number;
}

export interface ShiftDetailsEntry {
  date: string;
  shift: string;
  score: number;
  manager_id: string;
  servers: ServerShiftContribution[];
}

export interface ServerShiftContribution {
  server: string;
  greetings: number;
  score: number;
  premiumMenu: number;
  itemMention: number;
  itemDescription: number;
  drinkPromotions: number;
}

export interface StoreShiftDetailsEntry {
  date: string;
  shift: string;
  score: number;
  manager_id: string;
  store: string;
  servers: ServerShiftContribution[];
}

// Server Scorecard Data
export const serverScorecardData: ServerScorecardEntry[] = [
  {
    date: '2024-09-15',
    server: 'John Doe',
    greetings: 8,
    score: 85,
    premiumMenu: 95,
    itemMention: 92,
    itemDescription: 88,
    drinkPromotions: 75,
    lunch: 'Lunch',
  },
  {
    date: '2024-09-15',
    server: 'Jane Smith',
    greetings: 9,
    score: 92,
    premiumMenu: 98,
    itemMention: 97,
    itemDescription: 95,
    drinkPromotions: 90,
    lunch: 'Dinner',
  },
  {
    date: '2024-08-20',
    server: 'Mike Johnson',
    greetings: 7,
    score: 78,
    premiumMenu: 45,
    itemMention: 65,
    itemDescription: 70,
    drinkPromotions: 60,
    lunch: 'Lunch',
  }
];

// Cashier Scorecard Data
export const cashierScorecardData: CashierScorecardEntry[] = [
  {
    date: '2025-09-15',
    menuItem: 'Wagyu Steak',
    upsellAttempts: 15,
    averageUpsellRating: 8.5,
    attemptRatio: 85,
  },
  {
    date: '2025-09-15',
    menuItem: 'Lobster Thermidor',
    upsellAttempts: 12,
    averageUpsellRating: 9.2,
    attemptRatio: 92,
  },
  {
    date: '2025-09-15',
    menuItem: 'Chicken Caesar',
    upsellAttempts: 8,
    averageUpsellRating: 7.8,
    attemptRatio: 78,
  },
  {
    date: '2025-08-20',
    menuItem: 'Wagyu Steak',
    upsellAttempts: 10,
    averageUpsellRating: 8.0,
    attemptRatio: 80,
  },
  {
    date: '2025-08-20',
    menuItem: 'Lobster Thermidor',
    upsellAttempts: 9,
    averageUpsellRating: 8.8,
    attemptRatio: 88,
  }
];

// Greeter Scorecard Data
export const greeterScorecardData: GreeterScorecardEntry[] = [
  {
    date: '2025-09-15',
    rating1: 8.2,
    rating2: 7.8,
    rating3: 9.1,
    interactions: 45,
    averageRating: 8.4
  },
  {
    date: '2025-08-20',
    rating1: 7.5,
    rating2: 8.1,
    rating3: 8.8,
    interactions: 38,
    averageRating: 8.1
  }
];

export const greeterUpsellData: GreeterUpsellEntry[] = [
  {
    date: '2025-09-15',
    menuItem: 'Wagyu Steak',
    upsellAttempts: 12,
    averageUpsellRating: 8.5,
    attemptRatio: 85
  },
  {
    date: '2025-09-15',
    menuItem: 'Lobster Thermidor',
    upsellAttempts: 8,
    averageUpsellRating: 9.2,
    attemptRatio: 92
  },
  {
    date: '2025-09-15',
    menuItem: 'Truffle Pasta',
    upsellAttempts: 6,
    averageUpsellRating: 7.8,
    attemptRatio: 78
  },
  {
    date: '2025-08-20',
    menuItem: 'Wagyu Steak',
    upsellAttempts: 10,
    averageUpsellRating: 8.0,
    attemptRatio: 80
  },
  {
    date: '2025-08-20',
    menuItem: 'Lobster Thermidor',
    upsellAttempts: 7,
    averageUpsellRating: 8.8,
    attemptRatio: 88
  }
];

// Store Scorecard Data
export const storeScorecardData: StoreScorecardEntry[] = [
  {
    date: 'September 15, 2024',
    shift: 'Dinner',
    score: 92,
    manager_id: 'manager-001',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 14, 2024',
    shift: 'Lunch',
    score: 88,
    manager_id: 'manager-002',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 13, 2024',
    shift: 'Dinner',
    score: 95,
    manager_id: 'manager-001',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 12, 2024',
    shift: 'Lunch',
    score: 85,
    manager_id: 'manager-003',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 11, 2024',
    shift: 'Dinner',
    score: 90,
    manager_id: 'manager-002',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 10, 2024',
    shift: 'Lunch',
    score: 87,
    manager_id: 'manager-001',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 9, 2024',
    shift: 'Dinner',
    score: 93,
    manager_id: 'manager-003',
    location_id: 'location-001',
    location_name: 'Fremont',
  },
  {
    date: 'September 8, 2024',
    shift: 'Lunch',
    score: 89,
    manager_id: 'manager-002',
    location_id: 'location-001',
    location_name: 'Fremont',
  }
];

// Manager Scorecard Data
export const managerScorecardData: ManagerScorecardEntry[] = [
  {
    date: 'September 15, 2025',
    shift: 'Dinner',
    score: 92
  },
  {
    date: 'September 14, 2025',
    shift: 'Lunch',
    score: 88
  },
  {
    date: 'September 13, 2025',
    shift: 'Dinner',
    score: 95
  },
  {
    date: 'September 12, 2025',
    shift: 'Lunch',
    score: 85
  },
  {
    date: 'September 11, 2025',
    shift: 'Dinner',
    score: 90
  },
  {
    date: 'September 10, 2025',
    shift: 'Lunch',
    score: 87
  }
];

// Store-specific data for different locations
export const storeSpecificData: Record<string, StoreScorecardEntry[]> = {
  'Fremont': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 92,
      manager_id: 'manager-001',
      location_id: 'location-001',
      location_name: 'Fremont',
      greetings: 84,
      visit_count: 79, // < greetings → exercises the feat-092 collapse in fake mode
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 88,
      manager_id: 'manager-002',
      location_id: 'location-001',
      location_name: 'Fremont',
      greetings: 61,
      visit_count: 58,
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 95,
      manager_id: 'manager-001',
      location_id: 'location-001',
      location_name: 'Fremont',
      greetings: 73,
      visit_count: 70,
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 85,
      manager_id: 'manager-003',
      location_id: 'location-001',
      location_name: 'Fremont',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 90,
      manager_id: 'manager-002',
      location_id: 'location-001',
      location_name: 'Fremont',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 87,
      manager_id: 'manager-001',
      location_id: 'location-001',
      location_name: 'Fremont',
    },
    {
      date: 'September 9, 2024',
      shift: 'Dinner',
      score: 93,
      manager_id: 'manager-003',
      location_id: 'location-001',
      location_name: 'Fremont',
    },
    {
      date: 'September 8, 2024',
      shift: 'Lunch',
      score: 89,
      manager_id: 'manager-002',
      location_id: 'location-001',
      location_name: 'Fremont',
    }
  ],
  'Concord': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 88,
      manager_id: 'manager-004',
      location_id: 'location-002',
      location_name: 'Concord',
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 91,
      manager_id: 'manager-005',
      location_id: 'location-002',
      location_name: 'Concord',
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 89,
      manager_id: 'manager-004',
      location_id: 'location-002',
      location_name: 'Concord',
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 94,
      manager_id: 'manager-005',
      location_id: 'location-002',
      location_name: 'Concord',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 92,
      manager_id: 'manager-004',
      location_id: 'location-002',
      location_name: 'Concord',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 86,
      manager_id: 'manager-005',
      location_id: 'location-002',
      location_name: 'Concord',
    }
  ],
  'San Jose': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 90,
      manager_id: 'manager-006',
      location_id: 'location-003',
      location_name: 'San Jose',
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 87,
      manager_id: 'manager-007',
      location_id: 'location-003',
      location_name: 'San Jose',
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 93,
      manager_id: 'manager-006',
      location_id: 'location-003',
      location_name: 'San Jose',
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 89,
      manager_id: 'manager-007',
      location_id: 'location-003',
      location_name: 'San Jose',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 91,
      manager_id: 'manager-006',
      location_id: 'location-003',
      location_name: 'San Jose',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 88,
      manager_id: 'manager-007',
      location_id: 'location-003',
      location_name: 'San Jose',
    }
  ],
  'Oakland': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 85,
      manager_id: 'manager-008',
      location_id: 'location-004',
      location_name: 'Oakland',
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 92,
      manager_id: 'manager-009',
      location_id: 'location-004',
      location_name: 'Oakland',
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 88,
      manager_id: 'manager-008',
      location_id: 'location-004',
      location_name: 'Oakland',
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 90,
      manager_id: 'manager-009',
      location_id: 'location-004',
      location_name: 'Oakland',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 87,
      manager_id: 'manager-008',
      location_id: 'location-004',
      location_name: 'Oakland',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 94,
      manager_id: 'manager-009',
      location_id: 'location-004',
      location_name: 'Oakland',
    }
  ],
  'Berkeley': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 89,
      manager_id: 'manager-010',
      location_id: 'location-005',
      location_name: 'Berkeley',
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 91,
      manager_id: 'manager-011',
      location_id: 'location-005',
      location_name: 'Berkeley',
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 86,
      manager_id: 'manager-010',
      location_id: 'location-005',
      location_name: 'Berkeley',
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 93,
      manager_id: 'manager-011',
      location_id: 'location-005',
      location_name: 'Berkeley',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 88,
      manager_id: 'manager-010',
      location_id: 'location-005',
      location_name: 'Berkeley',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 92,
      manager_id: 'manager-011',
      location_id: 'location-005',
      location_name: 'Berkeley',
    }
  ],
  'Palo Alto': [
    {
      date: 'September 15, 2024',
      shift: 'Dinner',
      score: 94,
      manager_id: 'manager-012',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    },
    {
      date: 'September 14, 2024',
      shift: 'Lunch',
      score: 87,
      manager_id: 'manager-013',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    },
    {
      date: 'September 13, 2024',
      shift: 'Dinner',
      score: 91,
      manager_id: 'manager-012',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    },
    {
      date: 'September 12, 2024',
      shift: 'Lunch',
      score: 89,
      manager_id: 'manager-013',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    },
    {
      date: 'September 11, 2024',
      shift: 'Dinner',
      score: 92,
      manager_id: 'manager-012',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    },
    {
      date: 'September 10, 2024',
      shift: 'Lunch',
      score: 85,
      manager_id: 'manager-013',
      location_id: 'location-006',
      location_name: 'Palo Alto',
    }
  ]
};

// Leaderboard Data
export const leaderboardData: LeaderboardEntry[] = [
  {
    date: '2025-12-15',
    store: 'Fremont',
    score: 92,
    previousRank: 2,
    currentRank: 1
  },
  {
    date: '2025-12-15',
    store: 'Concord',
    score: 88,
    previousRank: 1,
    currentRank: 2
  },
  {
    date: '2025-12-15',
    store: 'San Jose',
    score: 85,
    previousRank: 3,
    currentRank: 3
  },
  {
    date: '2025-12-15',
    store: 'Oakland',
    score: 82,
    previousRank: 4,
    currentRank: 4
  },
  {
    date: '2025-12-15',
    store: 'Berkeley',
    score: 79,
    previousRank: 5,
    currentRank: 5
  },
  {
    date: '2025-11-15',
    store: 'Fremont',
    score: 89,
    previousRank: 1,
    currentRank: 2
  },
  {
    date: '2025-11-15',
    store: 'Concord',
    score: 91,
    previousRank: 2,
    currentRank: 1
  },
  {
    date: '2025-11-15',
    store: 'San Jose',
    score: 87,
    previousRank: 4,
    currentRank: 3
  },
  {
    date: '2025-11-15',
    store: 'Oakland',
    score: 84,
    previousRank: 3,
    currentRank: 4
  },
  {
    date: '2025-11-15',
    store: 'Berkeley',
    score: 81,
    previousRank: 5,
    currentRank: 5
  },
  {
    date: '2025-10-15',
    store: 'Fremont',
    score: 87,
    previousRank: 2,
    currentRank: 1
  },
  {
    date: '2025-10-15',
    store: 'Concord',
    score: 85,
    previousRank: 1,
    currentRank: 2
  },
  {
    date: '2025-10-15',
    store: 'San Jose',
    score: 83,
    previousRank: 3,
    currentRank: 3
  },
  {
    date: '2025-10-15',
    store: 'Oakland',
    score: 80,
    previousRank: 4,
    currentRank: 4
  },
  {
    date: '2025-10-15',
    store: 'Berkeley',
    score: 77,
    previousRank: 5,
    currentRank: 5
  }
];

// Helper function to get available months from any data array
export const getAvailableMonths = (data: Array<{ date: string }>): string[] => {
  const months = new Set<string>();
  data.forEach(item => {
    if (item.date) {
      const date = new Date(item.date);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.add(monthYear);
    }
  });
  return Array.from(months).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
};

// Helper function to get the latest month from data
export const getLatestMonth = (data: Array<{ date: string }>): string => {
  const availableMonths = getAvailableMonths(data);
  return availableMonths.length > 0 ? availableMonths[0] : 'All';
};

// Performance History Data
export const serverPerformanceHistoryData: LegacyServerPerformanceHistoryEntry[] = [
  {
    date: 'December 15, 2024',
    shift: 'Dinner',
    greetings: 8,
    score: 87,
    premiumMenu: 92,
    itemMention: 78,
    itemDescription: 85,
    drinkPromotions: 80
  },
  {
    date: 'December 14, 2024',
    shift: 'Lunch',
    greetings: 9,
    score: 82,
    premiumMenu: 88,
    itemMention: 75,
    itemDescription: 90,
    drinkPromotions: 85
  },
  {
    date: 'December 13, 2024',
    shift: 'Dinner',
    greetings: 7,
    score: 90,
    premiumMenu: 95,
    itemMention: 88,
    itemDescription: 92,
    drinkPromotions: 87
  },
  {
    date: 'December 12, 2024',
    shift: 'Lunch',
    greetings: 8,
    score: 78,
    premiumMenu: 82,
    itemMention: 70,
    itemDescription: 85,
    drinkPromotions: 75
  },
  {
    date: 'December 11, 2024',
    shift: 'Dinner',
    greetings: 9,
    score: 88,
    premiumMenu: 90,
    itemMention: 85,
    itemDescription: 88,
    drinkPromotions: 82
  },
  {
    date: 'December 10, 2024',
    shift: 'Lunch',
    greetings: 6,
    score: 75,
    premiumMenu: 78,
    itemMention: 72,
    itemDescription: 80,
    drinkPromotions: 70
  },
  {
    date: 'December 9, 2024',
    shift: 'Dinner',
    greetings: 8,
    score: 92,
    premiumMenu: 95,
    itemMention: 90,
    itemDescription: 94,
    drinkPromotions: 88
  },
  {
    date: 'December 8, 2024',
    shift: 'Lunch',
    greetings: 7,
    score: 83,
    premiumMenu: 85,
    itemMention: 80,
    itemDescription: 87,
    drinkPromotions: 82
  }
];

export const cashierPerformanceHistoryData: CashierPerformanceHistoryEntry[] = [
  {
    date: 'December 15, 2024',
    shift: 'Dinner',
    upsellAttempts: 12,
    averageUpsellRating: 2.3,
    attemptRatio: 85
  },
  {
    date: 'December 14, 2024',
    shift: 'Lunch',
    upsellAttempts: 8,
    averageUpsellRating: 1.8,
    attemptRatio: 75
  },
  {
    date: 'December 13, 2024',
    shift: 'Dinner',
    upsellAttempts: 15,
    averageUpsellRating: 2.7,
    attemptRatio: 92
  },
  {
    date: 'December 12, 2024',
    shift: 'Lunch',
    upsellAttempts: 6,
    averageUpsellRating: 1.5,
    attemptRatio: 68
  },
  {
    date: 'December 11, 2024',
    shift: 'Dinner',
    upsellAttempts: 11,
    averageUpsellRating: 2.1,
    attemptRatio: 80
  },
  {
    date: 'December 10, 2024',
    shift: 'Lunch',
    upsellAttempts: 9,
    averageUpsellRating: 1.9,
    attemptRatio: 72
  },
  {
    date: 'December 9, 2024',
    shift: 'Dinner',
    upsellAttempts: 14,
    averageUpsellRating: 2.8,
    attemptRatio: 88
  },
  {
    date: 'December 8, 2024',
    shift: 'Lunch',
    upsellAttempts: 7,
    averageUpsellRating: 2.0,
    attemptRatio: 78
  },
  {
    date: 'December 7, 2024',
    shift: 'Dinner',
    upsellAttempts: 13,
    averageUpsellRating: 2.4,
    attemptRatio: 82
  },
  {
    date: 'December 6, 2024',
    shift: 'Lunch',
    upsellAttempts: 5,
    averageUpsellRating: 1.6,
    attemptRatio: 65
  }
];

export const greeterPerformanceHistoryData: GreeterPerformanceHistoryEntry[] = [
  {
    date: 'December 15, 2024',
    shift: 'Dinner',
    upsellAttempts: 15,
    averageUpsellRating: 2.4,
    attemptRatio: 88
  },
  {
    date: 'December 14, 2024',
    shift: 'Lunch',
    upsellAttempts: 12,
    averageUpsellRating: 2.1,
    attemptRatio: 82
  },
  {
    date: 'December 13, 2024',
    shift: 'Dinner',
    upsellAttempts: 18,
    averageUpsellRating: 2.7,
    attemptRatio: 92
  },
  {
    date: 'December 12, 2024',
    shift: 'Lunch',
    upsellAttempts: 10,
    averageUpsellRating: 1.8,
    attemptRatio: 75
  },
  {
    date: 'December 11, 2024',
    shift: 'Dinner',
    upsellAttempts: 14,
    averageUpsellRating: 2.3,
    attemptRatio: 85
  },
  {
    date: 'December 10, 2024',
    shift: 'Lunch',
    upsellAttempts: 11,
    averageUpsellRating: 2.0,
    attemptRatio: 78
  },
  {
    date: 'December 9, 2024',
    shift: 'Dinner',
    upsellAttempts: 16,
    averageUpsellRating: 2.6,
    attemptRatio: 90
  },
  {
    date: 'December 8, 2024',
    shift: 'Lunch',
    upsellAttempts: 9,
    averageUpsellRating: 1.9,
    attemptRatio: 72
  },
  {
    date: 'December 7, 2024',
    shift: 'Dinner',
    upsellAttempts: 13,
    averageUpsellRating: 2.2,
    attemptRatio: 80
  },
  {
    date: 'December 6, 2024',
    shift: 'Lunch',
    upsellAttempts: 8,
    averageUpsellRating: 1.7,
    attemptRatio: 68
  }
];

export const greeterGreetingPerformanceHistoryData: GreeterGreetingPerformanceHistoryEntry[] = [
  {
    date: 'December 15, 2024',
    shift: 'Dinner',
    rating1: 8.5,
    rating2: 8.0,
    rating3: 9.0,
    interactions: 25,
    averageRating: 8.5
  },
  {
    date: 'December 14, 2024',
    shift: 'Lunch',
    rating1: 7.5,
    rating2: 8.5,
    rating3: 8.5,
    interactions: 18,
    averageRating: 8.2
  },
  {
    date: 'December 13, 2024',
    shift: 'Dinner',
    rating1: 9.0,
    rating2: 9.0,
    rating3: 9.5,
    interactions: 30,
    averageRating: 9.2
  },
  {
    date: 'December 12, 2024',
    shift: 'Lunch',
    rating1: 7.0,
    rating2: 7.5,
    rating3: 8.0,
    interactions: 15,
    averageRating: 7.5
  },
  {
    date: 'December 11, 2024',
    shift: 'Dinner',
    rating1: 8.0,
    rating2: 8.5,
    rating3: 8.5,
    interactions: 22,
    averageRating: 8.3
  },
  {
    date: 'December 10, 2024',
    shift: 'Lunch',
    rating1: 7.5,
    rating2: 8.0,
    rating3: 8.5,
    interactions: 20,
    averageRating: 8.0
  },
  {
    date: 'December 9, 2024',
    shift: 'Dinner',
    rating1: 8.5,
    rating2: 9.0,
    rating3: 9.0,
    interactions: 28,
    averageRating: 8.8
  },
  {
    date: 'December 8, 2024',
    shift: 'Lunch',
    rating1: 7.0,
    rating2: 7.5,
    rating3: 8.0,
    interactions: 16,
    averageRating: 7.8
  },
  {
    date: 'December 7, 2024',
    shift: 'Dinner',
    rating1: 8.0,
    rating2: 8.0,
    rating3: 8.5,
    interactions: 24,
    averageRating: 8.2
  },
  {
    date: 'December 6, 2024',
    shift: 'Lunch',
    rating1: 6.5,
    rating2: 7.0,
    rating3: 7.5,
    interactions: 12,
    averageRating: 7.0
  }
];

// Shift Details Data
export const shiftDetailsData: ShiftDetailsEntry[] = [
  {
    date: 'September 15, 2024',
    shift: 'Dinner',
    score: 92,
    manager_id: 'manager-001',
    servers: [
      {
        server: 'John Doe',
        greetings: 8,
        score: 95,
        premiumMenu: 90,
        itemMention: 85,
        itemDescription: 88,
        drinkPromotions: 92
      },
      {
        server: 'Sarah Johnson',
        greetings: 9,
        score: 88,
        premiumMenu: 85,
        itemMention: 90,
        itemDescription: 87,
        drinkPromotions: 89
      },
      {
        server: 'Mike Chen',
        greetings: 7,
        score: 93,
        premiumMenu: 95,
        itemMention: 88,
        itemDescription: 91,
        drinkPromotions: 94
      }
    ]
  },
  {
    date: 'September 14, 2024',
    shift: 'Lunch',
    score: 88,
    manager_id: 'manager-002',
    servers: [
      {
        server: 'Emily Rodriguez',
        greetings: 8,
        score: 85,
        premiumMenu: 82,
        itemMention: 87,
        itemDescription: 84,
        drinkPromotions: 86
      },
      {
        server: 'David Kim',
        greetings: 9,
        score: 91,
        premiumMenu: 88,
        itemMention: 92,
        itemDescription: 89,
        drinkPromotions: 90
      }
    ]
  },
  {
    date: 'September 13, 2024',
    shift: 'Dinner',
    score: 95,
    manager_id: 'manager-001',
    servers: [
      {
        server: 'John Doe',
        greetings: 9,
        score: 97,
        premiumMenu: 95,
        itemMention: 96,
        itemDescription: 98,
        drinkPromotions: 97
      },
      {
        server: 'Sarah Johnson',
        greetings: 10,
        score: 93,
        premiumMenu: 91,
        itemMention: 94,
        itemDescription: 92,
        drinkPromotions: 95
      },
      {
        server: 'Mike Chen',
        greetings: 8,
        score: 95,
        premiumMenu: 97,
        itemMention: 93,
        itemDescription: 96,
        drinkPromotions: 98
      },
      {
        server: 'Emily Rodriguez',
        greetings: 9,
        score: 94,
        premiumMenu: 92,
        itemMention: 95,
        itemDescription: 93,
        drinkPromotions: 96
      }
    ]
  },
  {
    date: 'September 12, 2024',
    shift: 'Lunch',
    score: 85,
    manager_id: 'manager-003',
    servers: [
      {
        server: 'David Kim',
        greetings: 7,
        score: 82,
        premiumMenu: 80,
        itemMention: 83,
        itemDescription: 81,
        drinkPromotions: 84
      },
      {
        server: 'John Doe',
        greetings: 8,
        score: 88,
        premiumMenu: 85,
        itemMention: 87,
        itemDescription: 86,
        drinkPromotions: 89
      }
    ]
  },
  {
    date: 'September 11, 2024',
    shift: 'Dinner',
    score: 90,
    manager_id: 'manager-002',
    servers: [
      {
        server: 'Sarah Johnson',
        greetings: 8,
        score: 89,
        premiumMenu: 87,
        itemMention: 90,
        itemDescription: 88,
        drinkPromotions: 91
      },
      {
        server: 'Mike Chen',
        greetings: 9,
        score: 91,
        premiumMenu: 89,
        itemMention: 92,
        itemDescription: 90,
        drinkPromotions: 93
      }
    ]
  },
  {
    date: 'September 10, 2024',
    shift: 'Lunch',
    score: 87,
    manager_id: 'manager-004',
    servers: [
      {
        server: 'Emily Rodriguez',
        greetings: 7,
        score: 85,
        premiumMenu: 83,
        itemMention: 86,
        itemDescription: 84,
        drinkPromotions: 87
      },
      {
        server: 'David Kim',
        greetings: 8,
        score: 89,
        premiumMenu: 86,
        itemMention: 88,
        itemDescription: 87,
        drinkPromotions: 90
      }
    ]
  },
  {
    date: 'September 9, 2024',
    shift: 'Dinner',
    score: 93,
    manager_id: 'manager-001',
    servers: [
      {
        server: 'John Doe',
        greetings: 9,
        score: 94,
        premiumMenu: 92,
        itemMention: 95,
        itemDescription: 93,
        drinkPromotions: 96
      },
      {
        server: 'Sarah Johnson',
        greetings: 8,
        score: 92,
        premiumMenu: 90,
        itemMention: 93,
        itemDescription: 91,
        drinkPromotions: 94
      }
    ]
  },
  {
    date: 'September 8, 2024',
    shift: 'Lunch',
    score: 89,
    manager_id: 'manager-002',
    servers: [
      {
        server: 'Mike Chen',
        greetings: 8,
        score: 87,
        premiumMenu: 85,
        itemMention: 88,
        itemDescription: 86,
        drinkPromotions: 89
      },
      {
        server: 'Emily Rodriguez',
        greetings: 9,
        score: 91,
        premiumMenu: 88,
        itemMention: 90,
        itemDescription: 89,
        drinkPromotions: 92
      }
    ]
  }
];

// Store Shift Details Data
export const storeShiftDetailsData: StoreShiftDetailsEntry[] = [
  // Fremont Store Shifts
  {
    date: 'September 15, 2024',
    shift: 'Dinner',
    score: 92,
    manager_id: 'manager-001',
    store: 'Fremont',
    servers: [
      {
        server: 'John Doe',
        greetings: 8,
        score: 95,
        premiumMenu: 90,
        itemMention: 85,
        itemDescription: 88,
        drinkPromotions: 92
      },
      {
        server: 'Sarah Johnson',
        greetings: 9,
        score: 88,
        premiumMenu: 85,
        itemMention: 90,
        itemDescription: 87,
        drinkPromotions: 89
      },
      {
        server: 'Mike Chen',
        greetings: 7,
        score: 93,
        premiumMenu: 95,
        itemMention: 88,
        itemDescription: 91,
        drinkPromotions: 94
      }
    ]
  },
  {
    date: 'September 14, 2024',
    shift: 'Lunch',
    score: 88,
    manager_id: 'manager-002',
    store: 'Fremont',
    servers: [
      {
        server: 'Emily Rodriguez',
        greetings: 8,
        score: 85,
        premiumMenu: 82,
        itemMention: 87,
        itemDescription: 84,
        drinkPromotions: 86
      },
      {
        server: 'David Kim',
        greetings: 9,
        score: 91,
        premiumMenu: 88,
        itemMention: 92,
        itemDescription: 89,
        drinkPromotions: 90
      }
    ]
  },
  {
    date: 'September 13, 2024',
    shift: 'Dinner',
    score: 95,
    manager_id: 'manager-001',
    store: 'Fremont',
    servers: [
      {
        server: 'John Doe',
        greetings: 9,
        score: 97,
        premiumMenu: 95,
        itemMention: 96,
        itemDescription: 98,
        drinkPromotions: 97
      },
      {
        server: 'Sarah Johnson',
        greetings: 10,
        score: 93,
        premiumMenu: 91,
        itemMention: 94,
        itemDescription: 92,
        drinkPromotions: 95
      }
    ]
  },
  // Concord Store Shifts
  {
    date: 'September 15, 2024',
    shift: 'Dinner',
    score: 88,
    manager_id: 'manager-005',
    store: 'Concord',
    servers: [
      {
        server: 'Alex Rodriguez',
        greetings: 7,
        score: 85,
        premiumMenu: 82,
        itemMention: 87,
        itemDescription: 84,
        drinkPromotions: 86
      },
      {
        server: 'Maria Garcia',
        greetings: 8,
        score: 91,
        premiumMenu: 88,
        itemMention: 92,
        itemDescription: 89,
        drinkPromotions: 90
      }
    ]
  },
  {
    date: 'September 14, 2024',
    shift: 'Lunch',
    score: 82,
    manager_id: 'manager-004',
    store: 'Concord',
    servers: [
      {
        server: 'James Wilson',
        greetings: 6,
        score: 78,
        premiumMenu: 75,
        itemMention: 80,
        itemDescription: 77,
        drinkPromotions: 79
      },
      {
        server: 'Emma Thompson',
        greetings: 7,
        score: 86,
        premiumMenu: 83,
        itemMention: 88,
        itemDescription: 85,
        drinkPromotions: 87
      }
    ]
  },
  {
    date: 'September 13, 2024',
    shift: 'Dinner',
    score: 95,
    manager_id: 'manager-005',
    store: 'Concord',
    servers: [
      {
        server: 'Alex Rodriguez',
        greetings: 9,
        score: 97,
        premiumMenu: 95,
        itemMention: 96,
        itemDescription: 98,
        drinkPromotions: 97
      },
      {
        server: 'Maria Garcia',
        greetings: 8,
        score: 93,
        premiumMenu: 91,
        itemMention: 94,
        itemDescription: 92,
        drinkPromotions: 95
      }
    ]
  },
  // San Jose Store Shifts
  {
    date: 'September 15, 2024',
    shift: 'Dinner',
    score: 81,
    manager_id: 'manager-006',
    store: 'San Jose',
    servers: [
      {
        server: 'Robert Brown',
        greetings: 6,
        score: 78,
        premiumMenu: 75,
        itemMention: 80,
        itemDescription: 77,
        drinkPromotions: 79
      },
      {
        server: 'Jennifer Davis',
        greetings: 7,
        score: 84,
        premiumMenu: 81,
        itemMention: 86,
        itemDescription: 83,
        drinkPromotions: 85
      }
    ]
  },
  {
    date: 'September 14, 2024',
    shift: 'Lunch',
    score: 86,
    manager_id: 'manager-007',
    store: 'San Jose',
    servers: [
      {
        server: 'Robert Brown',
        greetings: 8,
        score: 88,
        premiumMenu: 85,
        itemMention: 90,
        itemDescription: 87,
        drinkPromotions: 89
      },
      {
        server: 'Jennifer Davis',
        greetings: 7,
        score: 84,
        premiumMenu: 81,
        itemMention: 86,
        itemDescription: 83,
        drinkPromotions: 85
      }
    ]
  }
];

// Helper function to filter data by month
export const filterDataByMonth = <T extends { date: string }>(
  data: T[],
  selectedMonth: string
): T[] => {
  if (selectedMonth === 'All') return data;
  return data.filter(item => {
    if (!item.date) return true;
    const itemDate = new Date(item.date);
    const itemMonthYear = itemDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return itemMonthYear === selectedMonth;
  });
};
